import { useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { BACKEND_URL } from '../config';

const API = BACKEND_URL;
const makeMediaUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${API}${url}`;
};
const sectionAnim = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
};

const PublicProfile = () => {
  const { username } = useParams();
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    if (!username) return;

    fetch(`${API}/api/profile/${encodeURIComponent(username)}`)
      .then(res => res.json())
      .then(data => {
        if (data?.success) setProfile(data.profile);
      })
      .catch(console.error);
  }, [username]);

  if (!profile) return <p style={{ textAlign: 'center' }}>Loading...</p>;

  if (profile.isVerified === false) {
    return (
      <div style={{ ...container, textAlign: 'center', marginTop: '4rem' }}>
        <h2>Profile Under Review</h2>
        <p style={{ color: '#666' }}>This profile is currently under review and will be visible once verified.</p>
      </div>
    );
  }

  return (
    <div style={container}>

      {/* HERO */}
      <motion.section
        initial="hidden"
        animate="visible"
        variants={sectionAnim}
        transition={{ duration: 0.6 }}
        style={hero}
      >
        <img
          src={makeMediaUrl(profile.photo)}
          alt={profile.name || profile.username}
          style={avatar}
          onError={(e) => { e.currentTarget.style.display = 'none'; }}
        />

        <div>
          <h1 style={name}>{profile.name}</h1>
          <p style={usernameStyle}>@{profile.username}</p>
          <p style={intro}>{profile.introduction}</p>

          <div style={links}>
            {profile.personalSocialLinks?.map((link, i) => (
              <a key={i} href={link.url} target="_blank" rel="noreferrer" style={{ marginRight: '15px', textDecoration: 'none', color: '#007bff', fontWeight: '500' }}>
                {link.platform || 'View Profile'} →
              </a>
            ))}
          </div>
        </div>
      </motion.section>

      {/* ABOUT COMPANY */}
      <Section title="Organization / Practice">
        <p>{profile.companyIntro}</p>

        <div style={metaGrid}>
          <span>Founded: {profile.yearStarted}</span>
          <span>Members: {profile.companyMembers}</span>
          <span>Business: {profile.natureOfBusiness}</span>
          <span>Location: {profile.officeLocation}</span>
        </div>

        {profile.companyLogo && (
          <img src={`${API}${profile.companyLogo}`} style={{ width: 120 }} />
        )}
      </Section>

      {/* GALLERY */}
      <Section title="Work & Media">
        <div style={gallery}>
          {profile.galleryPhotos?.map((url, i) => (
            <motion.img
              key={i}
              src={makeMediaUrl(url)}
              style={galleryImg}
              whileHover={{ scale: 1.03 }}
            />
          ))}
        </div>
      </Section>

      {/* BLOGS */}
      <Section title="Writing & Thought Leadership">
        {profile.blogs?.length ? profile.blogs.map((b, i) => (
          <div key={i} style={card}>
            <h4>{b.title}</h4>
            <p>{b.para}</p>
            {b.link && (
              <a href={b.link} target="_blank" rel="noreferrer">
                Read Article →
              </a>
            )}
          </div>
        )) : <p>No blogs published.</p>}
      </Section>

      {/* AWARDS */}
      <Section title="Awards & Recognition">
        {profile.awards?.map((a, i) => (
          <div key={i} style={card}>
            <strong>{a.title}</strong> • {a.year}
            <p>{a.otherInfo}</p>
          </div>
        ))}
      </Section>

      {/* HIRING */}
      <Section title="Availability">
        <strong>
          {profile.isHiring ? 'Open to opportunities' : 'Not hiring currently'}
        </strong>
      </Section>

    </div>
  );
};

/* ---------------- STYLES ---------------- */

const container = {
  maxWidth: 1000,
  margin: 'auto',
  padding: '40px 20px',
  fontFamily: 'Inter, sans-serif'
};

const hero = {
  display: 'flex',
  gap: 40,
  alignItems: 'center',
  marginBottom: 60
};

const avatar = {
  width: 170,
  height: 170,
  borderRadius: '50%',
  objectFit: 'cover'
};

const name = { fontSize: 32, marginBottom: 6 };
const usernameStyle = { color: '#666', marginBottom: 10 };
const intro = { fontSize: 16, lineHeight: 1.6 };

const links = { marginTop: 12 };

const metaGrid = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
  gap: 10,
  marginTop: 15
};

const gallery = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
  gap: 16
};

const galleryImg = {
  width: '100%',
  height: 180,
  objectFit: 'cover',
  borderRadius: 12
};

const card = {
  background: '#f8f9fb',
  padding: 16,
  borderRadius: 12,
  marginBottom: 15
};

const Section = ({ title, children }) => (
  <motion.section
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true }}
    variants={sectionAnim}
    transition={{ duration: 0.5 }}
    style={{ marginBottom: 50 }}
  >
    <h2 style={{ marginBottom: 15 }}>{title}</h2>
    {children}
  </motion.section>
);

export default PublicProfile;
